import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { TeamsApiService } from '../core/teams-api.service';

@Component({
  selector: 'app-player-team-create-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <section class="page">
      <h1>{{ t('player.teamCreate.title') }}</h1>
      <p class="hint">{{ t('player.teamCreate.subtitle') }}</p>

      @if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      }

      <form (ngSubmit)="submit()" #form="ngForm" class="form">
        <label>{{ t('player.teamCreate.name') }}
          <input type="text" name="name" required [(ngModel)]="name" />
        </label>
        <label>{{ t('player.teamCreate.logo') }}
          <input type="url" name="logoUrl" [(ngModel)]="logoUrl" />
        </label>
        <button type="submit" [disabled]="loading() || !form.valid">
          {{ loading() ? t('player.teamCreate.loading') : t('player.teamCreate.submit') }}
        </button>
      </form>

      <p><a routerLink="/player/teams">{{ t('player.teamCreate.viewTeams') }}</a> · <a routerLink="/player/invitations">{{ t('player.teamCreate.viewInvitations') }}</a></p>
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 620px; margin: 0 auto; border: 1px solid var(--line-soft); border-radius: 12px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    h1 { margin: 0 0 0.5rem; font-size: 1.4rem; color: var(--text-strong); }
    .hint { margin: 0 0 0.9rem; color: var(--text-muted); }
    .form { display: grid; gap: 0.75rem; }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; color: #dbe9ff; }
    input { padding: 0.55rem 0.6rem; border: 1px solid var(--line-soft); border-radius: 8px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); }
    button { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.6rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .state--error { color: var(--danger); }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class PlayerTeamCreatePageComponent {
  private readonly teamsApi = inject(TeamsApiService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected name = '';
  protected logoUrl = '';
  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected submit(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.teamsApi
      .createTeam({
        name: this.name.trim(),
        logo_url: this.logoUrl.trim()
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ team }) => {
          this.loading.set(false);
          void this.router.navigateByUrl(`/player/teams/${team.id}`);
        },
        error: (error) => {
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            Array.isArray(apiErrors) && apiErrors.length > 0
              ? apiErrors.join(' | ')
              : this.t('player.teamCreate.error')
          );
          this.loading.set(false);
        }
      });
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
